import { MessageBus } from '@23degrees/shared/bus';
import { HttpClient } from '@angular/common/http';
import { Injectable, TemplateRef, inject } from '@angular/core';

import { Content } from '@23degrees/shared-code/models';
import { ContentManager } from '@23degrees/shared/manager/content/content.manager';
import { ContentLock } from '@23degrees/shared/models/content-lock.model';
import { AuthQuery } from '@23degrees/stores/auth/auth.query';
import { hasWriteRights } from '@23degrees/utilities/content.utils';
import { MESSAGE_RECIPIENTS, REQUEST_MESSAGES } from '@config';
import { NotificationType, NotificationsService } from 'angular2-notifications';
import { EMPTY, Observable, Subject, of } from 'rxjs';
import { delay, map, takeUntil, tap } from 'rxjs/operators';
import { EnvironmentService } from '../env/env.service';
import { GlobalService } from '../global/global.service';

@Injectable({
  providedIn: 'root',
})
export class ContentLockService {
  private http = inject(HttpClient);
  private authQuery = inject(AuthQuery);
  private notificationService = inject(NotificationsService);
  private contentManager = inject(ContentManager);
  private globalService = inject(GlobalService);
  private messageBus = inject(MessageBus);

  private envService: EnvironmentService = inject(EnvironmentService);

  private locks: Map<string, ContentLock> = new Map();

  private time: number = 60 * 60 * 1000;

  private timeout$: Observable<any>;
  private restart$ = new Subject<void>();
  private stop$ = new Subject<void>();

  public lockTimeout$ = new Subject<void>();

  public lockTemplate: TemplateRef<any>;
  public unlockTemplate: TemplateRef<any>;
  public stillLockTemplate: TemplateRef<any>;

  private notificationSettings = {
    pauseOnHover: true,
  };

  API_V2_PATH = `${this.envService.APIV2}/content`;

  ngOnDestroy() {
    // console.log('[ContentLock][ngOnDestroy] destroyed');
    this.resetLocks();
  }
  getLocks(slug): ContentLock {
    return this.locks.get(slug);
  }

  checkContentLock(slug: string): Observable<ContentLock> {
    if (this.globalService.isServer()) {
      return EMPTY;
    }

    return this.http.get<{ error; payload; status }>(`${this.API_V2_PATH}/${slug}/lock`).pipe(
      map((response) => response.payload),
      tap(async (lock: ContentLock) => {
        if (lock) {
          console.log('[ContentLock][checkContentLock] lock active', slug);
          this.locks.set(slug, lock);

          if (lock.userSlug !== this.authQuery.user().slug) {
            if (this.stillLockTemplate) {
              this.notificationService.html(this.stillLockTemplate, NotificationType.Info, this.notificationSettings, 'info', { lock: lock });
            }
          }
        } else {
          if (this.locks.get(slug)) {
            console.log('[ContentLock][checkContentLock] lock inactive, refetch', slug);
            const content = await this.contentManager.getContentBySlug(slug);

            const { referenceID } = content.localStorage;
            this.messageBus.emit(
              REQUEST_MESSAGES.ReloadContentBySlug,
              [MESSAGE_RECIPIENTS.ContentSelector],
              {
                slug: slug,
                options: {
                  requestRenderData: false,
                },
              },
              referenceID,
            );
          }
          this.locks.delete(slug);
        }
      }),
    );
  }

  addContentLock(content: Content): Observable<ContentLock> {
    if (this.globalService.isServer()) {
      return EMPTY;
    }

    // const condition =
    //   content &&
    //   !content.injected &&
    //   !content.typeSpecific.isChild &&
    //   content.shares &&
    //   content.shares.length > 0 &&
    //   hasWriteRights(content, this.authQuery.user()) &&
    //   otherHasWriteRights(content, this.authQuery.user());

    // console.log(condition, otherHasWriteRights(content, this.authQuery.user()));

    if (
      content &&
      !content.injected &&
      !content.typeSpecific.isChild &&
      content.shares &&
      content.shares.length > 0 &&
      hasWriteRights(content, this.authQuery.user()) // &&
      // otherHasWriteRights(content, this.authQuery.user())
    ) {
      return this.http.post<{ error; payload; status }>(`${this.API_V2_PATH}/${content.slug}/lock`, {}).pipe(
        map((response) => response.payload),
        tap((lock) => {
          if (lock) {
            console.log('[ContentLock][addContentLock] added/updated lock', content.slug);
            if (!this.locks.has(content.slug)) {
              if (this.lockTemplate) {
                this.notificationService.html(this.lockTemplate, NotificationType.Info, this.notificationSettings, 'info', { lock: lock, content: content });
              }
            }
            this.restart$.next();
            this.timeout$ = of(true).pipe(
              delay(this.time),
              takeUntil(this.restart$),
              takeUntil(this.stop$),
              // finalize(() => {
              //   console.log('timer restart or killed');
              // })
            );
            this.timeout$.subscribe(() => {
              // console.log('[ContentLock][addContentLock] timeout occurred');
              this.lockTimeout$.next();
            });
            this.locks.set(content.slug, lock);

            // fromEvent(window, 'beforeunload')
            //   .pipe(takeUntil(this.restart$), takeUntil(this.stop$))
            //   .subscribe(() => {
            //     console.log('window/tab closing');
            //   });
          } else {
            console.log('[ContentLock][addContentLock] no lock', content.slug);
            this.locks.delete(content.slug);
          }
        }),
      );
    }
    return EMPTY;
  }

  updateContentLock(slug: string): Observable<any> {
    if (this.globalService.isServer()) {
      return EMPTY;
    }

    const currentUser = this.authQuery.user();
    const currentLock = this.locks.get(slug);
    if (currentLock && currentLock.userSlug === currentUser.slug) {
      console.log('[ContentLock][updateContentLock] update lock', slug);
      return this.http.patch(`${this.API_V2_PATH}/${slug}/lock`, {});
    }

    return EMPTY;
  }

  deleteContentLock(slug: string): Observable<boolean> {
    if (this.globalService.isServer()) {
      return of(false);
    }

    const currentUser = this.authQuery.user();
    const currentLock = this.locks.get(slug);
    if (currentLock && currentLock.userSlug === currentUser.slug) {
      return this.http.delete<{ error; payload: boolean; status }>(`${this.API_V2_PATH}/${slug}/lock`).pipe(
        map((response) => response.payload),
        tap((lock) => {
          if (lock) {
            console.log('[ContentLock][deleteContentLock] removed lock', slug);
            this.locks.delete(slug);
            this.stop$.next();
            if (this.unlockTemplate) {
              this.notificationService.html(this.unlockTemplate, NotificationType.Info, this.notificationSettings, 'info', { lock: lock });
            }
          } else {
            console.log('[ContentLock][deleteContentLock] no lock', slug);
          }
        }),
      );
    }
    if (!currentLock) {
      // console.log('[ContentLock][deleteContentLock] nothing to remove for:', slug, this.locks.get(slug));
    } else if (currentLock.userSlug !== currentUser.slug) {
      // console.log('[ContentLock][deleteContentLock] user doesnt have the lock:', slug, this.locks.get(slug));
    }
    return of(false);
  }

  async resetLocks() {
    if (this.globalService.isServer()) return;

    const keys = Array.from(this.locks.keys());
    // console.log('[ContentLock][resetLocks] clear content locks', Array.from(this.locks.keys()));

    await Promise.all(
      keys.map(async (slug) => {
        // console.log('[ContentLock][resetLocks] clear lock:', slug);
        return this.deleteContentLock(slug).toPromise();
      }),
    );

    //console.log('[ContentLock][resetLocks] clear done!');
  }
}
